import GameStore from './store.js';
import { qs, qsa, delegate, toggleHidden } from './utils/dom.js';
import debounce from './utils/debounce.js';
import SearchOverlay from './components/search-overlay.js';
import { renderGames } from './components/game-grid.js';
import { trackEvent } from './utils/analytics.js';
import { initGameEngagement } from './utils/engagement.js';
import { toggleFavorite, isFavorite } from './utils/storage.js';
import { initTooltips } from './utils/tooltip.js';
import { initHomePageLazyLoad } from './utils/lazy-load.js';

const initCardRails = () => {
  const rails = qsa('[data-card-rail]');
  if (!rails.length) return;

  rails.forEach(rail => {
    const track = rail.querySelector('[data-rail-track]') || rail.querySelector('.game-rail__inner');
    const prevWrap = rail.querySelector('[data-rail-prev]');
    const nextWrap = rail.querySelector('[data-rail-next]');
    const prevBtn = prevWrap?.querySelector('button');
    const nextBtn = nextWrap?.querySelector('button');
    if (!track || !prevBtn || !nextBtn) return;

    const measurements = {
      trackWidth: 0,
      step: 0,
    };

    const fallbackStep = width => Math.max(width * 0.9, 240);

    const measure = () => {
      const currentWidth = track.clientWidth || 0;
      measurements.trackWidth = currentWidth;

      const cards = track.querySelectorAll('.game-card');
      let cardSpan = 0;

      if (cards.length >= 2) {
        const first = cards[0];
        const second = cards[1];
        cardSpan = second.offsetLeft - first.offsetLeft;
        if (cardSpan <= 0) {
          cardSpan = second.offsetWidth || first.offsetWidth;
        }
      } else if (cards.length === 1) {
        cardSpan = cards[0].offsetWidth;
      }

      if (!cardSpan || Number.isNaN(cardSpan)) {
        cardSpan = fallbackStep(currentWidth);
      }

      const visible = cardSpan > 0 ? Math.max(1, Math.floor(currentWidth / cardSpan)) : 1;
      let step = cardSpan * visible;
      if (!Number.isFinite(step) || step <= 0) {
        step = fallbackStep(currentWidth);
      }

      measurements.step = step;
    };

    const getStep = () => {
      const currentWidth = track.clientWidth || 0;
      if (!measurements.step || Math.abs(currentWidth - measurements.trackWidth) > 1) {
        measure();
      }
      return measurements.step || fallbackStep(currentWidth);
    };

    const updateNav = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const hasOverflow = maxScroll > 4;
      prevWrap?.classList.toggle('is-hidden', !hasOverflow);
      nextWrap?.classList.toggle('is-hidden', !hasOverflow);
      if (!hasOverflow) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        prevWrap?.classList.add('is-disabled');
        nextWrap?.classList.add('is-disabled');
        return;
      }

      const atStart = track.scrollLeft <= 4;
      prevBtn.disabled = atStart;
      prevWrap?.classList.toggle('is-disabled', atStart);
      const atEnd = track.scrollLeft >= maxScroll - 4;
      nextBtn.disabled = atEnd;
      nextWrap?.classList.toggle('is-disabled', atEnd);
    };

    let pendingFrame = null;
    const runAnimationFrame = typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame.bind(window)
      : callback => setTimeout(callback, 16);

    const scheduleNavUpdate = () => {
      if (pendingFrame !== null) return;
      pendingFrame = runAnimationFrame(() => {
        pendingFrame = null;
        updateNav();
      });
    };

    const handleNav = direction => {
      const step = getStep();
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const nextPosition = Math.min(Math.max(track.scrollLeft + direction * step, 0), maxScroll);
      track.scrollTo({ left: nextPosition, behavior: 'smooth' });
      scheduleNavUpdate();
    };

    prevBtn.addEventListener('click', () => handleNav(-1));
    nextBtn.addEventListener('click', () => handleNav(1));

    const handleResize = () => {
      measure();
      scheduleNavUpdate();
    };

    const debouncedResize = debounce(handleResize, 150);
    track.addEventListener('scroll', scheduleNavUpdate, { passive: true });
    window.addEventListener('resize', debouncedResize);

    measure();
    updateNav();
  });
};

const formatCountLabel = count => {
  if (count === 0) return 'No games found';
  if (count === 1) return '1 game found';
  return `${count} games found`;
};

const parseJson = id => {
  const el = qs(`#${id}`);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (err) {
    console.error(`Failed to parse JSON from #${id}`, err);
    return null;
  }
};

const fetchJson = async url => {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

const loadGamesDataset = async () => {
  const cacheBuster = Date.now();
  let files = ['games.json'];
  try {
    const manifest = await fetchJson(`/data/games-manifest.json?t=${cacheBuster}`);
    if (manifest && Array.isArray(manifest.files) && manifest.files.length) {
      files = manifest.files;
    }
  } catch (err) {
    console.warn('[catalog] games-manifest unavailable, falling back to games.json', err);
  }

  const merged = new Map();
  let loadedChunks = 0;
  await Promise.all(
    files.map(async file => {
      try {
        const chunk = await fetchJson(`/data/${file}?t=${cacheBuster}`);
        if (Array.isArray(chunk)) {
          loadedChunks += 1;
          chunk.forEach(game => {
            if (game && game.slug && !merged.has(game.slug)) {
              merged.set(game.slug, game);
            }
          });
        }
      } catch (err) {
        console.error(`[catalog] failed to load ${file}`, err);
      }
    }),
  );

  if (!merged.size && loadedChunks === 0) {
    throw new Error('No game chunks available');
  }

  return Array.from(merged.values());
};

const SIDEBAR_COLLAPSE_KEY = 'app:sidebar-collapsed';
const COLLAPSE_BREAKPOINT = '(max-width: 992px)';

const initNavigation = () => {
  const toggle = qs('[data-nav-toggle]');
  const sidebar = qs('[data-sidebar]');
  const closeBtn = qs('[data-close-sidebar]');
  const shellMain = qs('[data-shell-main]');
  const collapseBtn = qs('[data-sidebar-collapse]');
  const collapseMedia = typeof window.matchMedia === 'function' ? window.matchMedia(COLLAPSE_BREAKPOINT) : null;
  const toggleLabel = toggle.querySelector('.sr-only');
  if (!toggle || !sidebar) return;

  if (collapseBtn) {
    const collapseIconPath = collapseBtn.dataset.iconCollapse || '';
    const expandIconPath = collapseBtn.dataset.iconExpand || '';
    const iconPathEl = collapseBtn.querySelector('[data-sidebar-icon]');
    const readCollapsePreference = () => {
      try {
        return window.localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === 'true';
      } catch (err) {
        console.warn('localStorage unavailable', err);
        return false;
      }
    };

    const persistCollapsePreference = value => {
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSE_KEY, String(value));
      } catch (err) {
        console.warn('failed to persist sidebar collapse state', err);
      }
    };

    let collapsePreference = readCollapsePreference();

    const applyCollapsedState = collapsed => {
      document.body.classList.toggle('sidebar-collapsed', collapsed);
      collapseBtn.setAttribute('aria-expanded', String(!collapsed));
      collapseBtn.setAttribute('aria-label', 'Open/Close sidebar');
      if (iconPathEl) {
        const path = collapsed ? expandIconPath || collapseIconPath : collapseIconPath || expandIconPath;
        if (path) {
          iconPathEl.setAttribute('d', path);
        }
      }
    };

    const canUseCollapsed = () => !collapseMedia || !collapseMedia.matches;

    const syncCollapsedState = () => {
      if (!canUseCollapsed()) {
        applyCollapsedState(false);
        return;
      }
      applyCollapsedState(collapsePreference);
    };

    syncCollapsedState();

    if (collapseMedia) {
      const handleMedia = () => syncCollapsedState();
      if (typeof collapseMedia.addEventListener === 'function') {
        collapseMedia.addEventListener('change', handleMedia);
      } else if (typeof collapseMedia.addListener === 'function') {
        collapseMedia.addListener(handleMedia);
      }
    }

    collapseBtn.addEventListener('click', () => {
      if (!canUseCollapsed()) {
        return;
      }
      collapsePreference = !collapsePreference;
      applyCollapsedState(collapsePreference);
      persistCollapsePreference(collapsePreference);
    });
  } else {
    document.body.classList.remove('sidebar-collapsed');
  }

  const setOpen = open => {
    sidebar.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.classList.toggle('is-active', open);
    const labelText = open ? 'Close navigation' : 'Open navigation';
    toggle.setAttribute('aria-label', labelText);
    if (toggleLabel) {
      toggleLabel.textContent = labelText;
    }
    document.body.classList.toggle('sidebar-open', open);
  };

  toggle.addEventListener('click', () => {
    const isOpen = sidebar.classList.contains('is-open');
    setOpen(!isOpen);
  });

  closeBtn?.addEventListener('click', () => setOpen(false));

  shellMain?.addEventListener('click', event => {
    if (!sidebar.classList.contains('is-open')) return;
    if (toggle.contains(event.target)) return;
    if (!sidebar.contains(event.target)) {
      setOpen(false);
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && sidebar.classList.contains('is-open')) {
      setOpen(false);
    }
  });

  const navLinks = qsa('[data-nav-match]');
  if (navLinks.length) {
    const normalizePath = value => {
      if (!value) return '/';
      const withLeading = value.startsWith('/') ? value : `/${value}`;
      if (withLeading === '/') return '/';
      return `${withLeading.replace(/\/{2,}/g, '/').replace(/\/?$/, '')}/`;
    };

    const setActiveState = (link, active) => {
      link.classList.toggle('is-active', active);
      const item = link.closest('li');
      if (item) {
        item.classList.toggle('is-active', active);
      }
      if (active) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    };

    const evaluateMatchRule = (rule, context) => {
      if (!rule) return false;
      if (rule.startsWith('path:')) {
        const target = normalizePath(rule.slice(5).trim() || '/');
        return context.path === target || context.path.startsWith(target);
      }
      if (rule.startsWith('hash:')) {
        const target = rule.slice(5).trim();
        if (!target) return false;
        return context.path === '/' && context.hash === target;
      }
      if (rule.startsWith('query:')) {
        const pair = rule.slice(6).trim();
        if (!pair) return false;
        const [key, value = ''] = pair.split('=');
        if (!key) return false;
        return context.search.get(key) === value;
      }
      return false;
    };

    const updateActiveNavigation = () => {
      const currentUrl = new URL(window.location.href);
      const context = {
        path: normalizePath(currentUrl.pathname),
        hash: currentUrl.hash,
        search: currentUrl.searchParams,
      };
      navLinks.forEach(link => {
        const matchAttr = link.dataset.navMatch || '';
        const rules = matchAttr
          .split('|')
          .map(item => item.trim())
          .filter(Boolean);
        let isActive = false;
        if (rules.length) {
          isActive = rules.some(rule => evaluateMatchRule(rule, context));
        } else {
          const targetHref = link.getAttribute('href');
          if (targetHref) {
            try {
              const targetUrl = new URL(targetHref, currentUrl.origin);
              const targetPath = normalizePath(targetUrl.pathname);
              isActive = context.path === targetPath;
            } catch (err) {
              isActive = false;
            }
          }
        }
        setActiveState(link, isActive);
      });
    };

    updateActiveNavigation();
    window.addEventListener('hashchange', updateActiveNavigation);
    window.addEventListener('popstate', updateActiveNavigation);
  }
};

const initBackToTop = () => {
  const control = qs('[data-back-to-top]');
  if (!control) return;

  const prefersReducedMotion = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
  const revealThreshold = 400;

  const updateVisibility = () => {
    const offset = window.pageYOffset || document.documentElement.scrollTop || 0;
    control.classList.toggle('is-visible', offset > revealThreshold);
  };

  const schedule = (() => {
    if (typeof window.requestAnimationFrame === 'function') {
      let ticking = false;
      return () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          updateVisibility();
          ticking = false;
        });
      };
    }
    return () => updateVisibility();
  })();

  control.addEventListener('click', () => {
    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  });

  updateVisibility();
  window.addEventListener('scroll', schedule, { passive: true });
};

const updateActiveChip = (chips, value) => {
  chips.forEach(chip => {
    const isActive = chip.dataset.filterValue === value;
    chip.classList.toggle('is-active', isActive);
    chip.setAttribute('aria-checked', String(isActive));
  });
};

const updateUrl = (state, options = {}) => {
  const { skipCategory = false } = options;
  const url = new URL(window.location.href);
  if (skipCategory) {
    url.searchParams.delete('category');
  } else if (state.category && state.category !== 'all') {
    url.searchParams.set('category', state.category);
  } else {
    url.searchParams.delete('category');
  }

  if (state.search) {
    url.searchParams.set('q', state.search);
  } else {
    url.searchParams.delete('q');
  }

  if (state.sort && state.sort !== 'latest') {
    url.searchParams.set('sort', state.sort);
  } else {
    url.searchParams.delete('sort');
  }

  if (state.page > 1) {
    url.searchParams.set('page', String(state.page));
  } else {
    url.searchParams.delete('page');
  }

  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', next);
};

const updateSearchUrl = (query, page = 1) => {
  const url = new URL(window.location.href);
  if (query) {
    url.searchParams.set('q', query);
  } else {
    url.searchParams.delete('q');
  }
  if (page > 1) {
    url.searchParams.set('page', String(page));
  } else {
    url.searchParams.delete('page');
  }
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, '', next);
};

const initListingPage = games => {
  const grid = qs('[data-game-grid]');
  if (!grid) return;

  const filterGroup = qs('[data-filter-categories]');
  const sortSelect = qs('[data-sort-select]');
  const inlineSearch = qs('[data-inline-search]');
  const pagination = qs('[data-pagination]');
  const prevPageBtn = qs('[data-page-prev]');
  const nextPageBtn = qs('[data-page-next]');
  const pageCurrent = qs('[data-page-current]');
  const pageTotal = qs('[data-page-total]');
  const pageJumpForm = qs('[data-page-jump]');
  const pageJumpInput = qs('[data-page-input]');
  const pageJumpSubmit = qs('[data-page-submit]');
  const emptyState = qs('[data-empty-state]');
  const resetBtn = qs('[data-reset-filters]');
  const categorySection = qs('[data-initial-category]');

  const perPage = 28;
  const params = new URLSearchParams(window.location.search);
  const categoryParam = params.get('category');
  const sortParam = params.get('sort');
  const searchParam = params.get('q');
  const pageParam = Number.parseInt(params.get('page'), 10);
  const initialCategory = categorySection
    ? categorySection.dataset.initialCategory
    : categoryParam || 'all';
  const isCategoryPage = Boolean(categorySection);
  const store = new GameStore(games, {
    category: initialCategory,
    perPage,
    sort: sortParam || 'latest',
    search: searchParam || '',
    page: Number.isNaN(pageParam) ? 1 : Math.max(pageParam, 1),
  });

  const chips = qsa('[data-filter-value]', filterGroup || document);
  updateActiveChip(chips, initialCategory || 'all');

  const overlay = new SearchOverlay(document, {
    onSearch: query => {
      if (inlineSearch) inlineSearch.value = query;
      store.setSearch(query);
    },
  });

  store.subscribe(snapshot => {
    const { state, visible, total, totalPages, hasMore } = snapshot;
    renderGames(grid, visible);

    toggleHidden(emptyState, total !== 0);
    if (pagination) {
      const showPagination = totalPages > 1;
      toggleHidden(pagination, !showPagination);
      if (pageCurrent) {
        pageCurrent.textContent = String(state.page);
      }
      if (pageTotal) {
        pageTotal.textContent = String(totalPages || 1);
      }
      if (prevPageBtn) {
        prevPageBtn.disabled = state.page <= 1;
      }
      if (nextPageBtn) {
        nextPageBtn.disabled = !hasMore;
      }
      if (pageJumpInput) {
        const statePageValue = String(state.page);
        const committedValue = pageJumpInput.dataset.committedValue;
        const isFocused = document.activeElement === pageJumpInput;
        if (!isFocused || committedValue !== statePageValue) {
          pageJumpInput.value = statePageValue;
          pageJumpInput.dataset.committedValue = statePageValue;
        }
        pageJumpInput.min = '1';
        pageJumpInput.max = String(Math.max(totalPages, 1));
        pageJumpInput.disabled = !showPagination;
      }
      if (pageJumpSubmit) {
        pageJumpSubmit.disabled = !showPagination;
      }
    }

    if (inlineSearch && inlineSearch.value !== state.search) {
      inlineSearch.value = state.search;
    }
    overlay?.setValue?.(state.search);
    if (sortSelect && sortSelect.value !== state.sort) {
      sortSelect.value = state.sort;
    }
    updateActiveChip(chips, state.category);
    updateUrl(state, { skipCategory: isCategoryPage });
  });

  if (filterGroup) {
    delegate(filterGroup, '[data-filter-value]', 'click', (event, target) => {
      event.preventDefault();
      const value = target.dataset.filterValue;
      store.setCategory(value);
      trackEvent('filter_category', { category: value });
    });
  }

  if (sortSelect) {
    sortSelect.addEventListener('change', event => {
      store.setSort(event.target.value);
      trackEvent('sort_change', { sort: event.target.value });
    });
  }

  if (inlineSearch) {
    inlineSearch.addEventListener(
      'input',
      debounce(event => {
        store.setSearch(event.target.value.trim());
      }, 250)
    );
  }

  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      store.resetFilters();
      trackEvent('filters_reset');
    });
  }

  if (prevPageBtn) {
    prevPageBtn.addEventListener('click', () => {
      store.prevPage();
      trackEvent('paginate_prev');
    });
  }

  if (nextPageBtn) {
    nextPageBtn.addEventListener('click', () => {
      store.nextPage();
      trackEvent('paginate_next');
    });
  }

  if (pageJumpForm && pageJumpInput) {
    pageJumpForm.addEventListener('submit', event => {
      event.preventDefault();
      const value = Number.parseInt(pageJumpInput.value, 10);
      if (Number.isNaN(value)) {
        const snapshot = store.getSnapshot();
        pageJumpInput.value = String(snapshot.state.page);
        return;
      }
      store.setPage(value);
      trackEvent('paginate_jump', { page: value });
    });
  }
};

const initSearchPage = games => {
  const grid = qs('[data-search-grid]');
  if (!grid) return;

  const summary = qs('[data-search-summary]');
  const title = qs('[data-search-title]');
  const countEl = qs('[data-search-count]');
  const emptyState = qs('[data-search-empty]');
  const emptyDefault = qs('[data-search-empty-default]');
  const emptyNoResults = qs('[data-search-empty-noresults]');
  const termEl = qs('[data-search-term]');
  const pagination = qs('[data-search-pagination]');
  const prevPageBtn = pagination?.querySelector('[data-page-prev]');
  const nextPageBtn = pagination?.querySelector('[data-page-next]');
  const pageCurrent = pagination?.querySelector('[data-page-current]');
  const pageTotal = pagination?.querySelector('[data-page-total]');
  const pageJumpForm = pagination?.querySelector('[data-page-jump]');
  const pageJumpInput = pagination?.querySelector('[data-page-input]');
  const pageJumpSubmit = pagination?.querySelector('[data-page-submit]');

  const perPage = 28;
  const params = new URLSearchParams(window.location.search);
  const initialQuery = params.get('q')?.trim() || '';
  const pageParam = Number.parseInt(params.get('page'), 10);
  const initialPage = Number.isNaN(pageParam) ? 1 : Math.max(pageParam, 1);

  const store = new GameStore(games, {
    perPage,
    search: initialQuery,
    page: initialPage,
  });

  let activeQuery = initialQuery;

  const resetPaginationInput = () => {
    if (!pageJumpInput) return;
    pageJumpInput.value = '';
    pageJumpInput.dataset.committedValue = '';
  };

  const showNoQueryState = () => {
    renderGames(grid, []);
    toggleHidden(emptyState, false);
    toggleHidden(emptyDefault, false);
    toggleHidden(emptyNoResults, true);
    toggleHidden(pagination, true);
    if (summary) summary.textContent = 'Enter a term to explore games.';
    if (title) title.textContent = 'Search the catalog';
    if (countEl) countEl.textContent = '';
    if (termEl) termEl.textContent = '';
    resetPaginationInput();
    updateSearchUrl('', 1);
  };

  const showNoResultsState = query => {
    renderGames(grid, []);
    toggleHidden(emptyState, false);
    toggleHidden(emptyDefault, true);
    toggleHidden(emptyNoResults, false);
    if (termEl) termEl.textContent = query;
    if (title) title.textContent = 'No results for "' + query + '"';
    if (summary) summary.textContent = 'No matches found for "' + query + '".';
    if (countEl) countEl.textContent = formatCountLabel(0);
    toggleHidden(pagination, true);
    if (pageJumpInput) {
      pageJumpInput.value = '1';
      pageJumpInput.dataset.committedValue = '1';
      pageJumpInput.disabled = true;
    }
    if (pageJumpSubmit) pageJumpSubmit.disabled = true;
    updateSearchUrl(query, 1);
  };

  const showResultsState = snapshot => {
    const { state, visible, total, totalPages, hasMore } = snapshot;
    const query = state.search.trim();
    if (termEl) termEl.textContent = query;
    if (title) title.textContent = 'Results for "' + query + '"';
    if (summary) summary.textContent = 'Search results for "' + query + '".';
    if (countEl) countEl.textContent = formatCountLabel(total);

    renderGames(grid, visible);
    toggleHidden(emptyState, true);
    toggleHidden(emptyDefault, true);
    toggleHidden(emptyNoResults, true);

    if (pagination) {
      const showPagination = totalPages > 1;
      toggleHidden(pagination, !showPagination);
      if (pageCurrent) pageCurrent.textContent = String(state.page);
      if (pageTotal) pageTotal.textContent = String(totalPages || 1);
      if (prevPageBtn) prevPageBtn.disabled = state.page <= 1;
      if (nextPageBtn) nextPageBtn.disabled = !hasMore;
      if (pageJumpInput) {
        const statePageValue = String(state.page);
        const committedValue = pageJumpInput.dataset.committedValue;
        const isFocused = document.activeElement === pageJumpInput;
        if (!isFocused || committedValue !== statePageValue) {
          pageJumpInput.value = statePageValue;
          pageJumpInput.dataset.committedValue = statePageValue;
        }
        pageJumpInput.min = '1';
        pageJumpInput.max = String(Math.max(totalPages, 1));
        pageJumpInput.disabled = !showPagination;
      }
      if (pageJumpSubmit) {
        pageJumpSubmit.disabled = !showPagination;
      }
    }

    updateSearchUrl(query, state.page);
  };

  store.subscribe(snapshot => {
    const query = activeQuery.trim();
    if (!query) {
      showNoQueryState();
      return;
    }

    if (snapshot.total === 0) {
      showNoResultsState(query);
      return;
    }

    showResultsState(snapshot);
  });

  const applyQuery = () => {
    const paramsRef = new URLSearchParams(window.location.search);
    const nextQuery = paramsRef.get('q')?.trim() || '';
    const nextPageParam = Number.parseInt(paramsRef.get('page'), 10);
    const nextPage = Number.isNaN(nextPageParam) ? 1 : Math.max(nextPageParam, 1);

    if (!nextQuery) {
      activeQuery = '';
      store.setState({ search: '', page: 1 });
      showNoQueryState();
      return;
    }

    activeQuery = nextQuery;
    store.setState({ search: nextQuery, page: nextPage });
  };

  applyQuery();
  window.addEventListener('popstate', applyQuery);

  if (prevPageBtn) {
    prevPageBtn.addEventListener('click', () => {
      store.prevPage();
      trackEvent('search_paginate_prev');
    });
  }

  if (nextPageBtn) {
    nextPageBtn.addEventListener('click', () => {
      store.nextPage();
      trackEvent('search_paginate_next');
    });
  }

  if (pageJumpForm && pageJumpInput) {
    pageJumpForm.addEventListener('submit', event => {
      event.preventDefault();
      const value = Number.parseInt(pageJumpInput.value, 10);
      const snapshot = store.getSnapshot();
      if (Number.isNaN(value)) {
        pageJumpInput.value = String(snapshot.state.page);
        pageJumpInput.dataset.committedValue = String(snapshot.state.page);
        return;
      }
      store.setPage(value);
      trackEvent('search_paginate_jump', { page: value });
    });
  }
};



const initGameDetail = game => {
  const favoritesBtn = qs('[data-favorite-toggle]');
  const label = qs('[data-favorite-label]');
  const shareBtn = qs('[data-share-game]');

  if (favoritesBtn && label) {
    const updateState = () => {
      const active = isFavorite(game.slug);
      favoritesBtn.classList.toggle('is-active', active);
      label.textContent = active ? 'Saved' : 'Save Game';
    };

    favoritesBtn.addEventListener('click', () => {
      toggleFavorite(game.slug);
      updateState();
      trackEvent('favorite_toggle', { slug: game.slug });
    });

    updateState();
  }

  if (shareBtn) {
    shareBtn.addEventListener('click', async () => {
      const shareData = {
        title: game.title,
        text: game.description,
        url: window.location.href,
      };
      if (navigator.share) {
        try {
          await navigator.share(shareData);
          trackEvent('share_native', { slug: game.slug });
        } catch (err) {
          console.warn('Share cancelled', err);
        }
      } else if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(shareData.url);
          shareBtn.textContent = 'Link copied';
          setTimeout(() => {
            shareBtn.textContent = 'Share with Friends';
          }, 2000);
          trackEvent('share_clipboard', { slug: game.slug });
        } catch (err) {
          console.error('Clipboard failed', err);
        }
      }
    });
  }

  const fullscreenBtn = qs('[data-fullscreen-toggle]');
  const fullscreenLabel = qs('[data-fullscreen-label]');
  const fullscreenContainer = qs('[data-game-frame]');
  const iframe = qs('[data-game-iframe]');
  initGameEngagement(iframe, game.slug);

  if (fullscreenBtn && fullscreenContainer) {
    const requestMethods = ['requestFullscreen', 'webkitRequestFullscreen', 'webkitRequestFullScreen', 'mozRequestFullScreen', 'msRequestFullscreen'];
    const exitMethods = ['exitFullscreen', 'webkitExitFullscreen', 'webkitCancelFullScreen', 'mozCancelFullScreen', 'msExitFullscreen'];
    const fullscreenElements = ['fullscreenElement', 'webkitFullscreenElement', 'webkitCurrentFullScreenElement', 'mozFullScreenElement', 'msFullscreenElement'];
    const candidates = [fullscreenContainer, iframe].filter(Boolean);

    const hasMethod = (target, methods) => methods.some(method => typeof target?.[method] === 'function');
    const targetWithSupport = candidates.find(candidate => hasMethod(candidate, requestMethods));

    if (!targetWithSupport) {
      fullscreenBtn.setAttribute('hidden', '');
    } else {
      const enterLabel = 'Fullscreen';
      const exitLabel = 'Exit fullscreen';

      const callMethod = (target, methods) => {
        for (const method of methods) {
          if (typeof target[method] === 'function') {
            try {
              const result = target[method]();
              return result instanceof Promise ? result : Promise.resolve(result);
            } catch (err) {
              return Promise.reject(err);
            }
          }
        }
        return Promise.reject(new Error('Fullscreen API not supported'));
      };

      const getFullscreenElement = () => {
        for (const prop of fullscreenElements) {
          const value = document[prop];
          if (value) return value;
        }
        return null;
      };

      const isActive = () => {
        const current = getFullscreenElement();
        return Boolean(current && fullscreenContainer.contains(current));
      };

      let lastActive = false;
      const setState = active => {
        const labelText = active ? exitLabel : enterLabel;
        fullscreenBtn.setAttribute('aria-pressed', active ? 'true' : 'false');
        fullscreenBtn.setAttribute('aria-label', labelText);
        if (fullscreenLabel) fullscreenLabel.textContent = labelText;
        fullscreenBtn.classList.toggle('is-active', active);
        fullscreenContainer.classList.toggle('is-fullscreen', active);
        lastActive = active;
      };

      const syncState = () => {
        const active = isActive();
        if (lastActive && !active) {
          trackEvent('fullscreen_exit', { slug: game.slug });
        }
        setState(active);
      };

      setState(isActive());

      const enterFullscreen = () => callMethod(targetWithSupport, requestMethods);
      const exitFullscreen = () => callMethod(document, exitMethods);

      fullscreenBtn.addEventListener('click', () => {
        if (isActive()) {
          exitFullscreen().catch(err => console.warn('Exit fullscreen failed', err));
        } else {
          enterFullscreen()
            .then(() => {
              trackEvent('fullscreen_enter', { slug: game.slug });
              setState(true);
            })
            .catch(err => {
              console.error('Fullscreen request failed', err);
            });
        }
      });

      const fullscreenEvents = ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'];
      fullscreenEvents.forEach(event => {
        document.addEventListener(event, syncState);
      });
    }
  }
};


const bootstrap = async () => {
  initNavigation();
  initBackToTop();
  initCardRails();
  initTooltips();

  // Initialize lazy loading for homepage
  const isHomePage = document.querySelector('[data-card-rail]') && !document.querySelector('[data-game-grid]');
  if (isHomePage) {
    initHomePageLazyLoad();
  }

  const needsListing = Boolean(document.querySelector('[data-game-grid]'));
  const needsSearch = Boolean(document.querySelector('[data-search-page]'));
  const requiresCatalog = needsListing || needsSearch;

  if (requiresCatalog) {
    if (needsSearch) {
      const summary = qs('[data-search-summary]');
      if (summary) summary.textContent = 'Loading games…';
    }

    let gamesData;
    let loadFailed = false;
    try {
      gamesData = await loadGamesDataset();
    } catch (err) {
      loadFailed = true;
      console.error('[catalog] failed to load dataset', err);
    }

    if (Array.isArray(gamesData)) {
      if (needsListing) {
        initListingPage(gamesData);
      }
      if (needsSearch) {
        initSearchPage(gamesData);
      }
    } else if (needsSearch || loadFailed) {
      const summary = qs('[data-search-summary]');
      const title = qs('[data-search-title]');
      const countEl = qs('[data-search-count]');
      if (summary) summary.textContent = 'Unable to load games right now.';
      if (title) title.textContent = 'Search unavailable';
      if (countEl) countEl.textContent = '';
    }
  }

  const gameDetail = parseJson('game-detail');
  if (gameDetail) {
    initGameDetail(gameDetail);
  }
};

document.addEventListener('DOMContentLoaded', () => {
  bootstrap().catch(err => console.error('[app] bootstrap failed', err));
});
